import type { CaptureMode, QualityMode } from '../types/product.types';

export type PlanCode = 'launch' | 'growth' | 'scale' | 'admin';

export const PLAN_PRICES: Record<string, { monthly: number; annual: number }> = {
  launch: { monthly: 49, annual: 490 },
  growth: { monthly: 149, annual: 1490 },
  scale: { monthly: 399, annual: 3990 },
};

export const PLAN_PRODUCT_QUOTAS: Record<string, number> = {
  launch: 15,
  growth: 60,
  scale: 250,
  admin: 999999,
};

export const PLAN_PREMIUM_GENERATIONS: Record<string, number> = {
  launch: 1,
  growth: 5,
  scale: 999999,
  admin: 999999,
};

/**
 * Multi-Angle Capture is available on Scale and Admin plans only.
 */
export function hasMultiAngleAccess(planCode: string | null | undefined): boolean {
  if (!planCode) return false;
  return planCode === 'scale' || planCode === 'admin';
}

/**
 * Studio Render is available on Growth, Scale, and Admin plans.
 */
export function hasStudioRenderAccess(planCode: string | null | undefined): boolean {
  if (!planCode) return false;
  return planCode === 'growth' || planCode === 'scale' || planCode === 'admin';
}

/**
 * Returns true if the selected modes constitute a Premium Generation.
 * Premium = Multi-Angle Capture OR Studio Render (or both).
 */
export function isPremiumGeneration(
  captureMode: CaptureMode,
  qualityMode: QualityMode,
): boolean {
  return captureMode === 'multi_angle_capture' || qualityMode === 'studio_render';
}

/**
 * Returns the number of included Premium Generations per billing period for a plan.
 */
export function getPremiumGenerationsIncluded(planCode: string | null | undefined): number {
  if (!planCode) return 0;
  return PLAN_PREMIUM_GENERATIONS[planCode] ?? 0;
}

/**
 * Get plan display name.
 */
export function getPlanDisplayName(planCode: string | null | undefined): string {
  const planNames: Record<string, string> = {
    launch: 'Launch',
    growth: 'Growth',
    scale: 'Scale',
    admin: 'Admin',
  };
  return planNames[planCode || ''] || 'Unknown Plan';
}

/**
 * Legacy: check if plan has turbo/express access.
 * Kept for backward compatibility — prefer hasStudioRenderAccess or hasMultiAngleAccess.
 */
export function hasTurboAccess(planCode: string | null | undefined): boolean {
  return hasStudioRenderAccess(planCode);
}

/**
 * Check if plan is premium (Growth, Scale, or Admin).
 */
export function isPremiumPlan(planCode: string | null | undefined): boolean {
  return hasStudioRenderAccess(planCode);
}
